/**
 * useGridRowAction
 *
 * Composable that provides safe, accessible row click and keyboard activation
 * handlers for Kendo UI for Vue Grid instances.
 *
 * Highlights:
 * - Guards against triggers when clicking interactive elements (buttons, links, inputs, checkboxes).
 * - Suppresses activation when the user is highlighting/selecting text inside a cell.
 * - Restricts mouse actions to primary (left) click.
 * - Provides keyboard activation (Enter / Space) for focused grid rows.
 * - Action-agnostic: executes consumer-supplied `onRowAction` callback with `dataItem` and context.
 */

import type { Ref } from "vue";
import type { GridRowClickEvent } from "../types/kendo";

export interface RowActionCoordinates {
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
  screenX: number;
  screenY: number;
}

export interface RowActionContext<T = any> {
  /**
   * Data item associated with the activated row.
   */
  dataItem: T;
  /**
   * Row index if provided by Kendo event or row DOM attribute.
   */
  rowIndex?: number;
  /**
   * Field name if click originated on a specific column cell.
   */
  field?: string;
  /**
   * Original native browser event.
   */
  event: Event;
  /**
   * Interaction trigger type ('click' | 'keyboard').
   */
  triggerType: "click" | "keyboard";
  /**
   * Modifier key states during activation.
   */
  ctrlKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /**
   * Pointer coordinates of the activation. Derived from the native mouse event
   * for clicks, or from the focused row's bounding box for keyboard activation.
   * `undefined` when neither source is available.
   */
  coordinates?: RowActionCoordinates;
}

export interface UseGridRowActionOptions<T = any> {
  /**
   * Action callback invoked when a row is clicked or activated via keyboard.
   */
  onRowAction: (dataItem: T, context: RowActionContext<T>) => void;
  /**
   * Optional ref to Kendo Grid component instance or array/ref of data items.
   * Used to resolve the row `dataItem` when row is activated via grid-level `@keydown`.
   */
  gridRef?: Ref<any>;
  dataItems?: Ref<T[]> | T[];
  /**
   * Additional CSS selectors inside row cells that should prevent the row action.
   */
  ignoreSelectors?: string[];
  /**
   * Custom predicate function to check if an event target should be ignored.
   * Return `true` to ignore row action, `false` to proceed.
   */
  shouldIgnoreTarget?: (target: HTMLElement, event: Event) => boolean;
  /**
   * Whether to enable Enter/Space keyboard activation on focused grid rows.
   * Default: true
   */
  enableKeyboardAction?: boolean;
}

export interface UseGridRowActionReturn<T = any> {
  /**
   * Grid row click handler to bind to Kendo Grid's `@rowclick` event.
   */
  handleRowClick: (event: GridRowClickEvent<T>) => void;
  /**
   * Grid keyboard handler to bind to Kendo Grid's `@keydown` event.
   * Optionally accepts explicit `dataItem` if bound at the row/slot level.
   */
  handleRowKeyDown: (event: KeyboardEvent, explicitDataItem?: T) => void;
  /**
   * Helper function to test if a given DOM element is an ignored target.
   */
  isIgnoredTarget: (target: HTMLElement, event?: Event) => boolean;
}

const DEFAULT_IGNORE_SELECTORS = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  ".k-checkbox",
  ".k-button",
  ".k-dropdown",
  ".k-picker",
  ".k-hierarchy-cell",
  ".k-grid-header-menu",
  ".k-column-menu",
];

export const useGridRowAction = <T = any>(
  options: UseGridRowActionOptions<T>,
): UseGridRowActionReturn<T> => {
  const {
    onRowAction,
    gridRef,
    dataItems,
    ignoreSelectors = [],
    shouldIgnoreTarget,
    enableKeyboardAction = true,
  } = options;

  const combinedIgnoreSelectors = [
    ...DEFAULT_IGNORE_SELECTORS,
    ...ignoreSelectors,
  ];

  /**
   * Tests if an event target element or any of its parent elements match ignored selectors
   * or custom predicate filters.
   */
  const isIgnoredTarget = (target: HTMLElement, event?: Event): boolean => {
    if (!target) return false;

    // Check if element or ancestor matches any ignored selector
    const selectorString = combinedIgnoreSelectors.join(",");
    if (target.closest && target.closest(selectorString)) {
      return true;
    }

    // Check custom predicate if provided
    if (shouldIgnoreTarget && event) {
      return shouldIgnoreTarget(target, event);
    }

    return false;
  };

  /**
   * Reads the row index from the nearest data row element.
   */
  const resolveRowIndex = (target?: HTMLElement | null): number | undefined => {
    const rowEl = target?.closest?.(
      ".k-table-row[data-grid-row-index]",
    ) as HTMLElement | null;
    const attr = rowEl?.getAttribute("data-grid-row-index");
    if (attr === null || attr === undefined) return undefined;
    const parsed = parseInt(attr, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  /**
   * Extracts pointer coordinates from a native mouse/pointer event.
   */
  const getEventCoordinates = (
    nativeEvent: any,
  ): RowActionCoordinates | undefined => {
    if (!nativeEvent || typeof nativeEvent.clientX !== "number") {
      return undefined;
    }
    const scrollX = typeof window !== "undefined" ? window.scrollX || 0 : 0;
    const scrollY = typeof window !== "undefined" ? window.scrollY || 0 : 0;
    const screenX = typeof window !== "undefined" ? window.screenX || 0 : 0;
    const screenY = typeof window !== "undefined" ? window.screenY || 0 : 0;
    return {
      clientX: nativeEvent.clientX,
      clientY: nativeEvent.clientY,
      pageX:
        typeof nativeEvent.pageX === "number"
          ? nativeEvent.pageX
          : nativeEvent.clientX + scrollX,
      pageY:
        typeof nativeEvent.pageY === "number"
          ? nativeEvent.pageY
          : nativeEvent.clientY + scrollY,
      screenX:
        typeof nativeEvent.screenX === "number"
          ? nativeEvent.screenX
          : nativeEvent.clientX + screenX,
      screenY:
        typeof nativeEvent.screenY === "number"
          ? nativeEvent.screenY
          : nativeEvent.clientY + screenY,
    };
  };

  /**
   * Derives coordinates from an element's bounding box (keyboard activation).
   */
  const getElementCoordinates = (
    element: HTMLElement | null,
  ): RowActionCoordinates | undefined => {
    if (!element || typeof element.getBoundingClientRect !== "function") {
      return undefined;
    }
    const rect = element.getBoundingClientRect();
    if (!rect) return undefined;
    const clientX = rect.left;
    const clientY = rect.bottom;
    const scrollX = typeof window !== "undefined" ? window.scrollX || 0 : 0;
    const scrollY = typeof window !== "undefined" ? window.scrollY || 0 : 0;
    return {
      clientX,
      clientY,
      pageX: clientX + scrollX,
      pageY: clientY + scrollY,
      screenX: clientX,
      screenY: clientY,
    };
  };

  /**
   * Checks if user currently has active text selected inside the target row or container.
   */
  const hasTextSelection = (target?: HTMLElement | null): boolean => {
    if (typeof window === "undefined") return false;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      return false;
    }
    if (target && selection.anchorNode && selection.focusNode) {
      const container =
        target.closest(".k-table-row") || target.closest(".k-grid") || target;
      const anchorParent =
        selection.anchorNode.nodeType === Node.ELEMENT_NODE
          ? (selection.anchorNode as HTMLElement)
          : selection.anchorNode.parentElement;
      const focusParent =
        selection.focusNode.nodeType === Node.ELEMENT_NODE
          ? (selection.focusNode as HTMLElement)
          : selection.focusNode.parentElement;
      const isAnchorInside = !!(
        anchorParent && container.contains(anchorParent)
      );
      const isFocusInside = !!(focusParent && container.contains(focusParent));
      return isAnchorInside || isFocusInside;
    }
    return true;
  };

  /**
   * Primary handler for Kendo Grid `@rowclick` event.
   */
  const handleRowClick = (event: GridRowClickEvent<T>): void => {
    const nativeEvent =
      (event as any)?.htmlEvent ||
      (event as any)?.event ||
      (event as any)?.nativeEvent ||
      (event instanceof Event ? event : undefined);

    // Only allow primary (left) mouse clicks
    if (
      nativeEvent &&
      nativeEvent.button !== undefined &&
      nativeEvent.button !== 0
    ) {
      return;
    }

    // Guard against ignored child element targets
    const target = (nativeEvent?.target ||
      (event as any)?.target) as HTMLElement | null;
    if (
      target &&
      isIgnoredTarget(target, nativeEvent || (event as unknown as Event))
    ) {
      return;
    }

    // Guard against active text highlighting / selection within the row/grid
    if (hasTextSelection(target)) {
      return;
    }

    const context: RowActionContext<T> = {
      dataItem: event.dataItem,
      rowIndex: event.rowIndex ?? resolveRowIndex(target),
      field: event.field,
      event: nativeEvent || (event as unknown as Event),
      triggerType: "click",
      ctrlKey: !!nativeEvent?.ctrlKey,
      shiftKey: !!nativeEvent?.shiftKey,
      metaKey: !!nativeEvent?.metaKey,
      altKey: !!nativeEvent?.altKey,
      coordinates:
        getEventCoordinates(nativeEvent) ?? getElementCoordinates(target),
    };

    onRowAction(event.dataItem, context);
  };

  /**
   * Primary handler for Grid `@keydown` event (Enter / Space on row).
   */
  const handleRowKeyDown = (
    event: KeyboardEvent,
    explicitDataItem?: T,
  ): void => {
    if (!enableKeyboardAction) return;

    const key = event.key || event.code;
    const isActivationKey =
      key === "Enter" ||
      key === "NumpadEnter" ||
      key === " " ||
      key === "Space" ||
      event.code === "Enter" ||
      event.code === "NumpadEnter" ||
      event.code === "Space";

    if (!isActivationKey) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Check if event target is inside a data row
    const rowEl = target.closest(
      ".k-table-row[data-grid-row-index], .k-table-row",
    ) as HTMLElement | null;
    if (!rowEl) return;

    // Guard against ignored child element targets
    if (isIgnoredTarget(target, event)) {
      return;
    }

    // Prevent default scrolling or unintended form submissions on Space / Enter
    event.preventDefault();
    event.stopPropagation();

    // Resolve row index from DOM attribute if available
    const rowIndexAttr = rowEl.getAttribute("data-grid-row-index");
    const rowIndex =
      rowIndexAttr !== null ? parseInt(rowIndexAttr, 10) : undefined;

    // Resolve data item: explicitDataItem -> dataItems option -> gridRef option
    let resolvedDataItem: T | undefined = explicitDataItem;

    if (resolvedDataItem === undefined) {
      const itemsList =
        (Array.isArray(dataItems) ? dataItems : dataItems?.value) ||
        (gridRef?.value as any)?.props?.dataItems ||
        (gridRef?.value as any)?.dataItems;

      if (
        itemsList &&
        rowIndex !== undefined &&
        rowIndex >= 0 &&
        rowIndex < itemsList.length
      ) {
        resolvedDataItem = itemsList[rowIndex];
      }
    }

    if (resolvedDataItem === undefined) return;

    const context: RowActionContext<T> = {
      dataItem: resolvedDataItem,
      rowIndex,
      event,
      triggerType: "keyboard",
      ctrlKey: !!event.ctrlKey,
      shiftKey: !!event.shiftKey,
      metaKey: !!event.metaKey,
      altKey: !!event.altKey,
      coordinates: getElementCoordinates(rowEl),
    };

    onRowAction(resolvedDataItem, context);
  };

  return {
    handleRowClick,
    handleRowKeyDown,
    isIgnoredTarget,
  };
};
